import { PricingBreakdown } from "@/components/pricing/pricing-breakdown";
import { formatMoney } from "@/components/wallet/shop-format";
import type { PricingSnapshot } from "@/lib/pricing/types";
import { cn } from "@/lib/utils";

/**
 * One "what you pay" breakdown, shared by the item sheet and the cart so both
 * show money the same way. GST is displayed as *included* (India launch prices
 * are tax-inclusive), so it is not added into the total — only a separately
 * disclosed delivery fee (and any exclusive tax) is.
 */
export function CheckoutSummary({
  subtotalMinor,
  taxIncludedMinor = 0,
  taxAddedMinor = 0,
  deliveryFeeMinor,
  totalMinor,
  currency,
  showDelivery = true,
  className,
  pricingSnapshot,
  totalLabel = "Total",
}: {
  subtotalMinor: number;
  taxIncludedMinor?: number;
  taxAddedMinor?: number;
  deliveryFeeMinor: number;
  totalMinor: number;
  currency: string;
  /** Hide the delivery row for in-person pickup. */
  showDelivery?: boolean;
  className?: string;
  pricingSnapshot?: PricingSnapshot | null;
  totalLabel?: string;
}) {
  return (
    <dl
      className={cn(
        "rounded-xl border border-border bg-surface/60 p-3 text-sm",
        className,
      )}
    >
      {pricingSnapshot ? (
        <PricingBreakdown snapshot={pricingSnapshot} showTotal={false} />
      ) : (
        <>
          <Row label="Subtotal" value={formatMoney(subtotalMinor, currency)} />
          {taxIncludedMinor > 0 ? (
            <Row
              label="Tax (included)"
              value={formatMoney(taxIncludedMinor, currency)}
              muted
            />
          ) : null}
          {taxAddedMinor > 0 ? (
            <Row label="Tax" value={formatMoney(taxAddedMinor, currency)} />
          ) : null}
        </>
      )}
      {showDelivery && !pricingSnapshot ? (
        <Row
          label="Delivery"
          value={
            deliveryFeeMinor > 0
              ? formatMoney(deliveryFeeMinor, currency)
              : "Free"
          }
        />
      ) : null}
      <div className="mt-1.5 border-t border-border pt-1.5">
        <Row
          label={totalLabel}
          value={formatMoney(totalMinor, currency)}
          bold
        />
      </div>
    </dl>
  );
}

function Row({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <dt
        className={cn(
          muted ? "text-text-tertiary" : "text-text-secondary",
          bold && "font-semibold text-text-primary",
        )}
      >
        {label}
      </dt>
      <dd
        className={cn(
          "tabular-nums",
          muted ? "text-text-tertiary" : "text-text-secondary",
          bold && "font-bold text-text-primary",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
